import _ from 'lodash';
import JSZip from 'jszip';
import { DateTime, Info } from 'luxon';
import { combinations, generateRoundRobinSchedule } from '@/helpers/combinatorics';


class SchedulerService {
  /**
   * Create an array of dates and times for specified range
   * @param {String} Start Date
   * @param {String} End Date
   * @param {Object} Weekly Schedule
   * @returns An array of {@code DateTime} objects for scheduling games with.
   */
  createGameDates(startDate, endDate, Schedule) {
    let result = [], seasonWeekNumber = 1, currentDate = DateTime.fromJSDate(startDate),
      _endDate = DateTime.fromJSDate(endDate);

    while (currentDate <= _endDate.plus({ weeks: 1 })) {
      let currentWeek = DateTime.fromObject(
        { weekNumber: currentDate.weekNumber, weekYear: currentDate.weekYear });

      for (let [weekDay, gameTimes] of Object.entries(Schedule)) {
        let weekDayNumber = Info.weekdays().indexOf(weekDay) + 1;
        if (weekDayNumber < currentDate.weekday) continue;

        for (let gameTime of gameTimes) {
          let gameDateTime = this.createGameDayObject(currentWeek, weekDayNumber, gameTime);

          if (gameDateTime.date.startOf('day') <= _endDate.startOf('day')) {
            if (!(seasonWeekNumber % 2) && gameTime.fixedMatch && gameTime.biweekly) continue;

            result.push(gameDateTime);
          }
        }
      }

      currentDate = currentWeek.plus({ weeks: 1 });
      seasonWeekNumber++;
    }

    return result.sort();
  }

  /**
   * Create game day object for creating a new {@code DateTime} object.
   * @param {DateTime} Current week 
   * @param {Number} Week day
   * @param {Object} Game time 
   * @returns An object containing the game time.
   */
  createGameDayObject(currentWeek, weekDay, gameTime) {
    let dateObject =
      { weekYear: currentWeek.weekYear, weekNumber: currentWeek.weekNumber, weekDay: weekDay };

    return {
      fixedMatch: gameTime.fixedMatch,
      date: DateTime.fromObject({ ...dateObject, ...{ hour: gameTime.hour, minute: gameTime.minute } }),
      selected: true,
      holiday: false,
    };
  }
  // Original round robin methods -----
  // /**
  //  * Match the opposite vertices of a polygon.
  //  * @param {Array} vertices 
  //  * @param {Number} n
  //  * @param {Number} round
  //  * @returns An array of matched vertices.
  //  */
  // matchVertices(vertices, n, round) {
  //   let result = [];

  //   for (let i = 0; i < parseInt(Math.floor(n / 2)) - 1; i++) {
  //     let match = [vertices[i], vertices[vertices.length - 1 - i]];

  //     if (!(round % 2)) _.reverse(match);
  //     result.push(match);
  //   }

  //   return result;
  // }

  // /**
  //  * Rotate the teams and then match them against one another using the round robin algorithm.
  //  * @param {Array} teams 
  //  * @param {Number} rotation 
  //  * @param {Number} round 
  //  * @returns An array of matches for the current round robin iteration.
  //  */
  // createMatches(teams, rotation, round) {
  //   let result = [], vertices = teams.slice();

  //   if (!(teams.length % 2)) {
  //     let first, last = vertices.pop(), reverse = !(round % 2) ? !(rotation % 2) : rotation % 2;

  //     this.rotate(vertices, rotation);

  //     first = vertices.shift();
  //     result.push(reverse ? [first, last] : [last, first]);
  //   } else {
  //     this.rotate(vertices, rotation);
  //     vertices.shift();
  //   }

  //   result = [...result, ...this.matchVertices(vertices, teams.length, round)];

  //   return result;
  // }
  // /**
  //  * Rotate an array of data to the left by a specified number of times.
  //  * @param {Array} arr Array to rotate
  //  * @param {Number} rotation Number of rotations
  //  */
  // rotate(arr, rotation) {
  //   for (let i = 0; i < rotation; i++) {
  //     arr.push(arr.shift());
  //   }
  // }
  // end Original round robin methods -----

  /**
   * Assign created matches to pre-determined rink dates.
   * @param {Array} gameDates 
   * @param {Array} matches 
   * @param {Array} fixedTeams
   * @returns An array of assigned games with the supplied list of dates.
   */
  assignGameDates(gameDates, matches, fixedTeams) {
    let result = [];

    // assign fixed matches
    let fixedTeamIndex = 0;
    for (let gameTime of gameDates.filter(gt => gt.fixedMatch)) {
      if (!fixedTeams.length) break;
      let fixedTeam = fixedTeams[fixedTeamIndex];
      fixedTeamIndex = ++fixedTeamIndex % fixedTeams.length;

      result.push({
        homeTeam: fixedTeam,
        awayTeam: fixedTeam,
        gameTime: gameTime.date,
        fixedMatch: true
      });
    }

    let dateTeams = {}; // matched teams that have played on a date
    // assign 9:30 pm games first to make them even.
    assignMatchesWithConstraints(gameDates.filter(gt => !gt.fixedMatch && gt.date.toFormat('HH:mm') == '21:30'));
    assignMatchesWithConstraints(gameDates.filter(gt => !gt.fixedMatch && gt.date.toFormat('HH:mm') != '21:30'));

    return result;
    // -------------------------------------------------- helper functions
    function assignMatchesWithConstraints(gameSlots) {
      for (let gameTime of gameSlots) {
        let gameDay = gameTime.date.toFormat('yyyy-MM-dd'),
          yesterday = gameTime.date.minus({ days: 1 }).toFormat('yyyy-MM-dd');

        if (typeof dateTeams[gameDay] == 'undefined') dateTeams[gameDay] = [];

        let matchIndex = getFittingMatchIndex(matches, gameDay, yesterday);
        if (matchIndex == -1) {
          // console.error('unable to assign all matches with the given constraints.');
          continue;
        }
        let matchTeams = matches.splice(matchIndex, 1)[0];
        dateTeams[gameDay] = [...dateTeams[gameDay], ...matchTeams];
        result.push({
          homeTeam: matchTeams[0],
          awayTeam: matchTeams[1],
          gameTime: gameTime.date,
          fixedMatch: false
        });
      }
    }
    function getFittingMatchIndex(matches, gameDay, yesterday) {
      for (let [i, matchTeams] of Object.entries(matches)) {
        // find teams that have not played today or yesterday
        if (playedOnDate(matchTeams, gameDay) ||
          playedOnDate(matchTeams, yesterday)) continue;
        return parseInt(i);
      }
      return -1;
    }
    function playedOnDate(teams, date) {
      if (typeof dateTeams[date] == 'undefined') return false;
      return arraysIntersect(teams, dateTeams[date]);
    }
    function arraysIntersect(arr1, arr2) {
      return arr1.filter(item => arr2.includes(item)).length > 0;
    }
  } // assignGameDates() ------------------------------------------------------

  /**
   * Create the game schedule.
   * Builds complete round robin tournaments, assigns them to dates, then fills
   * any leftover slots while keeping teams as balanced as possible.
   * @param {Array} gameDates Game dates.
   * @param {Array} teams Team list
   * @param {Number} randomLevel Randomness
   */
  createSchedule(gameDates, teams, randomLevel = 1) {
    let matches = [],
      fixedTeams = teams.filter(team => team.fixed).map(team => team.name),
      matchedTeams = teams.filter(team => !team.fixed).map(team => team.name),
      matchedGameDates = gameDates.filter(gameDate => !gameDate.fixedMatch),
      numTournamentGames = combinations(matchedTeams.length, 2),
      numFullTournaments = Math.floor(matchedGameDates.length / numTournamentGames);

    // requires randomization because of constraints. fails without any.
    if (randomLevel == 1) randomLevel = 2; // requires randomization because of constraints

    let schedule, i = 0, maxTries = 30;
    do {
      matches = this.createMatchSequence(matchedTeams, numFullTournaments, randomLevel);
      schedule = this.assignGameDates(gameDates, matches, fixedTeams);
      i++;
    } while (matches.length > 0 && i < maxTries)

    // fill any remaining empty slots while keeping teams as balanced as possible
    schedule = this.fillRemainingSlots(schedule, gameDates, matchedTeams);
    schedule.sort((a, b) => a.gameTime - b.gameTime);

    return schedule;
  }

  createMatchSequence(teams, numTournaments, randomLevel) { // --> [Match, ... ]
    // generates a sequence of n round robin tournaments with randomization applied
    //    tournaments are not broken up. e.g. [...[tournament1], ...[tournament2], ...)
    //    and round matches are kept together.
    //    This helps the date assignment step not end with un-assignable matches due to constraints.
    // definitions:
    //  Tournament: all teams play each other once
    //  Round: all teams play once (some teams get a bye if odd number of teams)
    //  Match: one game between two teams
    // what is randomized
    //  A) the order of teams going into the round robin generation
    //  B) the order of matches within a round
    //  C) the order of the rounds 
    // randomLevel:
    //  1 - no randomization
    //  2 - randomize team order only (A)
    //  3 - randomize team order and matches within rounds (A+B)
    //  4 - randomize team order, matches within rounds, and rounds order (A+B+C)
    let matches = [];
    for (let i = 0; i < numTournaments; i++) {
      // A) conditionally shuffle team order before generating the tournament
      let tournamentTeams = randomLevel >= 2 ? _.shuffle(teams.slice()) : teams;
      // Round robin schedule generation
      let tournamentRounds = generateRoundRobinSchedule(tournamentTeams);
      // B) conditionally shuffle matches within each round
      if (randomLevel >= 3) {
        for (const i in tournamentRounds) {
          tournamentRounds[i] = _.shuffle(tournamentRounds[i]);
        }
      }
      // C) conditionally shuffle the order of the rounds
      tournamentRounds = randomLevel >= 4 ? _.shuffle(tournamentRounds) : tournamentRounds;
      // accumulate flattened matches
      matches = [...matches, ...tournamentRounds.flat()];
    }
    return matches;
  }

  /**
   * Fill any leftover empty slots after the round robin tournaments are assigned.
   * Picks matchups that keep the schedule as balanced as possible by preferring
   * teams with the fewest games, then matchup pairs played fewest times.
   * Respects same-day and back-to-back-day constraints.
   * @param {Array} schedule Current schedule of assigned games
   * @param {Array} gameDates All available game date slots
   * @param {Array} matchedTeams Tournament team names
   * @returns {Array} Schedule with leftover slots filled
   */
  fillRemainingSlots(schedule, gameDates, matchedTeams) {
    let assignedTimes = new Set(schedule.map(g => g.gameTime.toISO()));

    // collect unfilled non-fixed slots
    let emptySlots = gameDates.filter(gd =>
      !gd.fixedMatch && !assignedTimes.has(gd.date.toISO()));

    if (!emptySlots.length) return schedule;

    // build tallies from the current schedule
    let teamGameCount = {};
    for (let team of matchedTeams) teamGameCount[team] = 0;
    let matchupCount = {};
    let dateTeams = {};
    for (let game of schedule) {
      let day = game.gameTime.toFormat('yyyy-MM-dd');
      if (!dateTeams[day]) dateTeams[day] = [];
      if (game.fixedMatch) continue;
      teamGameCount[game.homeTeam] = (teamGameCount[game.homeTeam] || 0) + 1;
      teamGameCount[game.awayTeam] = (teamGameCount[game.awayTeam] || 0) + 1;
      let key = [game.homeTeam, game.awayTeam].sort().join('|');
      matchupCount[key] = (matchupCount[key] || 0) + 1;
      dateTeams[day].push(game.homeTeam, game.awayTeam);
    }

    // all possible matchup pairs
    let allPairs = [];
    for (let i = 0; i < matchedTeams.length; i++) {
      for (let j = i + 1; j < matchedTeams.length; j++) {
        allPairs.push([matchedTeams[i], matchedTeams[j]]);
      }
    }

    emptySlots.sort((a, b) => a.date - b.date);

    for (let slot of emptySlots) {
      let gameDay = slot.date.toFormat('yyyy-MM-dd');
      let yesterday = slot.date.minus({ days: 1 }).toFormat('yyyy-MM-dd');
      let tomorrow = slot.date.plus({ days: 1 }).toFormat('yyyy-MM-dd');
      // block teams that already play the same day or an adjacent day
      let blocked = new Set([
        ...(dateTeams[gameDay] || []),
        ...(dateTeams[yesterday] || []),
        ...(dateTeams[tomorrow] || [])
      ]);

      let validPairs = allPairs.filter(p => !blocked.has(p[0]) && !blocked.has(p[1]));
      if (!validPairs.length) continue; // leave empty only if truly impossible

      // prefer teams with fewer games, then matchups played fewer times
      validPairs.sort((a, b) => {
        let totalA = teamGameCount[a[0]] + teamGameCount[a[1]];
        let totalB = teamGameCount[b[0]] + teamGameCount[b[1]];
        if (totalA !== totalB) return totalA - totalB;
        let keyA = [a[0], a[1]].sort().join('|');
        let keyB = [b[0], b[1]].sort().join('|');
        return (matchupCount[keyA] || 0) - (matchupCount[keyB] || 0);
      });

      // random tiebreak among equally-best candidates
      let bestTotal = teamGameCount[validPairs[0][0]] + teamGameCount[validPairs[0][1]];
      let bestKey = validPairs[0].slice().sort().join('|');
      let bestMatchup = matchupCount[bestKey] || 0;
      let topPairs = validPairs.filter(p =>
        (teamGameCount[p[0]] + teamGameCount[p[1]]) === bestTotal &&
        (matchupCount[p.slice().sort().join('|')] || 0) === bestMatchup);

      let pair = topPairs.length > 1 ? _.sample(topPairs) : topPairs[0];

      schedule.push({
        homeTeam: pair[0],
        awayTeam: pair[1],
        gameTime: slot.date,
        fixedMatch: false
      });

      // update tallies
      teamGameCount[pair[0]]++;
      teamGameCount[pair[1]]++;
      let key = pair.slice().sort().join('|');
      matchupCount[key] = (matchupCount[key] || 0) + 1;
      if (!dateTeams[gameDay]) dateTeams[gameDay] = [];
      dateTeams[gameDay].push(pair[0], pair[1]);
    }

    return schedule;
  }

  /**
   * Download the generated game schedules.
   * @param {Array} Teams
   * @param {Array} Schedule
   */
  downloadSchedules(teams, schedule) {
    let headers = ['Date', 'Time', 'Home', 'Away'],
      scheduleData = [
        headers.join(','),
        ...schedule.map(game => [
          `"${game.gameTime.toFormat('MM/dd/yyyy')}"`,
          `"${game.gameTime.toFormat('hh:mm a')}"`,
          game.homeTeam, game.awayTeam,
        ].join(','))
      ],
      scheduleListFiles = {
        'schedule.csv': scheduleData.join('\n'),
        ...teams.reduce((accumulator, team) => {
          let teamSchedule = scheduleData.slice().filter(
            (row, index) => !index || row.indexOf(team.name) != -1).join('\n');

          return {
            ...accumulator,
            [`${team.name.toLowerCase()}.csv`]: teamSchedule
          };
        }, {})
      };

    this.createDownload('schedule.zip', scheduleListFiles);
  }
  // TEAMSNAP format
  // downloadSchedules(teams, schedule) {
  //   // teamsnap format https://helpme.teamsnap.com/article/1839-importing-team-schedules
  //   let headers = ['Date', 'Time', 'Duration (HH:MM)', 'Arrival Time (Minutes)', 'Name', 'Opponent Name',
  //     "Opponent Contact Name", "Opponent Contact Phone Number", "Opponent Contact E-mail Address",
  //     "Location Name", "Location Address", "Location Details", "Location URL", "Home or Away",
  //     "Uniform", "Extra Label", "Notes"
  //   ],
  //     scheduleData = [headers.join(','), ...schedule.map(game => {
  //       return [
  //         `"${game.gameTime.toFormat('MM/dd/yyyy')}"`,
  //         `"${game.gameTime.toFormat('hh:mm a')}"`,
  //         '"01:30"', '"30"',
  //         game.homeTeam, game.awayTeam,
  //         '', '', '', 'Arbutus Club', '', '', '', 'h', '', '', ''
  //       ].join(',')
  //     })],
  //     scheduleListFiles = {
  //       'schedule.csv': scheduleData.join('\n'),
  //       ...teams.reduce((accumulator, team) => {
  //         let teamSchedule = scheduleData.slice().filter(
  //           (row, index) => !index || row.indexOf(team.name) != -1).join('\n');

  //         return {
  //           ...accumulator,
  //           [`${team.name.toLowerCase()}.csv`]: teamSchedule
  //         };
  //       }, {})
  //     };

  //   this.createDownload('schedule.zip', scheduleListFiles);
  // }
  // ORIGINAL
  // downloadSchedules(teams, schedule) {
  //   let headers = ['Home', 'Away', 'Date/Time'],
  //     scheduleData = [headers.join(','), ...schedule.map(game => {
  //       return [game.homeTeam, game.awayTeam, `"${game.gameTime.toFormat('ffff')}"`].join(',')
  //     })],
  //     scheduleListFiles = {
  //       'schedule.csv': scheduleData.join('\n'),
  //       ...teams.reduce((accumulator, team) => {
  //         let teamSchedule = scheduleData.slice().filter(
  //           (row, index) => !index || row.indexOf(team.name) != -1).join('\n');

  //         return {
  //           ...accumulator,
  //           [`${team.name.toLowerCase()}.csv`]: teamSchedule
  //         };
  //       }, {})
  //     };

  //   this.createDownload('schedule.zip', scheduleListFiles);
  // }

  /**
   * Create a downloadable a file from a text string.
   * @param {String} Filename 
   * @param {String} Schedule lists
   */
  createDownload(filename, scheduleListFiles) {
    let zipFile = new JSZip(), zipFolder = zipFile.folder('schedule');

    for (let [filename, data] of Object.entries(scheduleListFiles)) {
      zipFolder.file(filename, data);
    }

    zipFile.generateAsync({ type: 'base64' }).then((zipFileData) => {
      let element = document.createElement('a');

      element.setAttribute(
        'href', `data:application/zip;base64,${encodeURIComponent(zipFileData)}`);
      element.setAttribute('download', filename);

      element.style.display = 'none';

      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    });
  }
}

let schedulerService = new SchedulerService();

export default schedulerService;
